/**
 * Offline affordances in the top bar: a connection pill, and the outbox behind it.
 *
 * The pill is always present rather than appearing only when something is wrong —
 * a control that materialises unpredictably is read as an error, and staff need to be
 * able to check "is my work saved?" at any moment, not only when the app decides to
 * tell them.
 */

import * as React from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  CloudOff,
  RefreshCw,
  Trash2,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react';
import { OFFLINE_OPERATION_SPECS, type OutboxEntry } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger, Separator, Tooltip } from '@/components/ui/misc';
import { EmptyState } from '@/components/common/page-header';
import { discard, retryEntry } from '@/lib/offline/outbox';
import { useOffline } from '@/lib/offline/context';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function OfflineIndicator() {
  const { t } = useI18n();
  const { isOnline, entries, pendingCount, failedCount, isSyncing, syncNow } = useOffline();

  const hasQueue = entries.length > 0;

  // Nothing queued and connected — a single quiet dot, no chrome.
  const tone = !isOnline ? 'critical' : failedCount > 0 ? 'serious' : pendingCount > 0 ? 'warning' : 'good';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('gap-1.5 px-2', !isOnline && 'text-critical')}
          aria-label={isOnline ? t('offline.online') : t('offline.offline')}
        >
          {isOnline ? <Wifi className="size-4" /> : <WifiOff className="size-4" />}
          {hasQueue && (
            <Badge tone={tone} size="sm" className="tabular">
              {entries.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-start justify-between gap-2 border-b px-3 py-2.5">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-semibold">
              {isOnline ? (
                <>
                  <span className="bg-good size-1.5 rounded-full" aria-hidden />
                  {t('offline.online')}
                </>
              ) : (
                <>
                  <span className="bg-critical size-1.5 rounded-full" aria-hidden />
                  {t('offline.offline')}
                </>
              )}
            </p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {isOnline ? t('offline.onlineHint') : t('offline.offlineHint')}
            </p>
          </div>

          {isOnline && pendingCount > 0 && (
            <Button variant="outline" size="sm" loading={isSyncing} onClick={() => void syncNow()}>
              <RefreshCw /> {t('offline.syncNow')}
            </Button>
          )}
        </div>

        {!hasQueue ? (
          <EmptyState
            icon={Check}
            title={t('offline.allSynced')}
            hint={isOnline ? undefined : t('offline.readOnlyHint')}
            compact
          />
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {entries.map((entry) => (
              <OutboxRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}

        <Separator />
        <p className="text-muted-foreground px-3 py-2 text-[11px] leading-snug">
          {t('offline.policyHint')}
        </p>
      </PopoverContent>
    </Popover>
  );
}

function OutboxRow({ entry }: { entry: OutboxEntry }) {
  const { t, dateTime } = useI18n();
  const spec = OFFLINE_OPERATION_SPECS[entry.operation];
  const isFailed = entry.status === 'failed';

  /**
   * Discarding an outbox entry throws away work the operator did with no connection, and it
   * is the one delete in the app with nothing to recover from — so it asks first.
   *
   * Confirmed inline rather than through the shared ConfirmDialog because this row lives
   * inside a Popover: a dialog portals to the body and traps focus, the popover reads that
   * as an outside interaction and closes, and closing unmounts the dialog that was asking
   * the question. A two-step button inside the row cannot lose that fight.
   */
  const [confirming, setConfirming] = React.useState(false);

  return (
    <div className="flex items-start gap-2.5 border-b px-3 py-2.5 last:border-b-0">
      <div
        className={cn(
          'mt-0.5 grid size-6 shrink-0 place-items-center rounded-full',
          isFailed ? 'bg-[var(--tint-critical-bg)] text-critical' : 'bg-muted text-muted-foreground',
        )}
      >
        {isFailed ? <AlertTriangle className="size-3" /> : <CloudOff className="size-3" />}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium">{t(spec.labelKey)}</p>
        <p className="text-muted-foreground truncate text-xs">{entry.summary}</p>
        <p className="text-muted-foreground/70 mt-0.5 text-[11px]">
          {dateTime(entry.createdAt)}
          {entry.attempts > 0 && ` · ${t('offline.attempts', { n: entry.attempts })}`}
        </p>
        {isFailed && entry.error && !confirming && (
          <p className="text-critical mt-1 text-[11px]">{entry.error}</p>
        )}
        {confirming && (
          <p className="text-critical mt-1 text-[11px] font-medium">
            {t('confirm.discardChange')} {t('confirm.discardChangeHint')}
          </p>
        )}
      </div>

      {isFailed && (
        <div className="flex shrink-0 flex-col gap-0.5">
          {confirming ? (
            <>
              <Tooltip content={t('action.delete')}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    void discard(entry.id);
                    toast.success(t('common.deleted'));
                  }}
                  aria-label={t('action.delete')}
                >
                  <Check className="text-destructive" />
                </Button>
              </Tooltip>
              <Tooltip content={t('action.cancel')}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setConfirming(false)}
                  aria-label={t('action.cancel')}
                >
                  <X />
                </Button>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip content={t('action.retry')}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void retryEntry(entry.id)}
                  aria-label={t('action.retry')}
                >
                  <RefreshCw />
                </Button>
              </Tooltip>
              <Tooltip content={t('action.delete')}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setConfirming(true)}
                  aria-label={t('action.delete')}
                >
                  <Trash2 className="text-destructive" />
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Full-width bar shown only while disconnected. The pill covers the steady state; this
 * exists because losing the connection mid-task needs to interrupt, not wait to be
 * noticed.
 */
export function OfflineBanner() {
  const { t } = useI18n();
  const { isOnline, pendingCount } = useOffline();

  if (isOnline) return null;

  return (
    <div
      role="status"
      className="print:hidden bg-[var(--tint-critical-bg)] border-[var(--tint-critical-border)] text-critical flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 border-b px-4 py-1.5 text-[13px]"
    >
      <span className="flex items-center gap-1.5 font-medium">
        <WifiOff className="size-3.5" />
        {t('offline.bannerTitle')}
      </span>
      <span className="text-critical/85">
        {pendingCount === 0
          ? t('offline.bannerHint')
          : // Greek marks the singular differently; one queued action is not "1 ενέργειες".
            pendingCount === 1
            ? t('offline.bannerQueuedOne')
            : t('offline.bannerQueued', { n: pendingCount })}
      </span>
    </div>
  );
}

/** Inline marker on a record that exists only in the local queue so far. */
export function PendingSyncBadge({ className }: { className?: string }) {
  const { t } = useI18n();
  return (
    <Tooltip content={t('offline.pendingHint')}>
      <Badge tone="warning" size="sm" className={cn('gap-1', className)}>
        <CloudOff className="size-3" />
        {t('offline.pending')}
      </Badge>
    </Tooltip>
  );
}

/**
 * Wraps an action that requires a connection. Renders the trigger disabled with an
 * explanation while offline, rather than letting it fail on click.
 */
export function RequiresConnection({
  reasonKey,
  children,
}: {
  reasonKey: string;
  children: React.ReactElement;
}) {
  const { t } = useI18n();
  const { isOnline } = useOffline();

  if (isOnline) return children;

  return (
    <Tooltip content={`${t('offline.requiresConnection')} — ${t(reasonKey)}`}>
      <span className="inline-flex cursor-not-allowed">
        {React.cloneElement(children, { disabled: true } as Partial<unknown>)}
      </span>
    </Tooltip>
  );
}
